import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { API_URL } from '../config';
import { getValidImageUrl } from '../utils/getImageUrl';
import { CartContext } from '../context/CartContext';
import { WishlistContext } from '../context/WishlistContext';
import Spinner from '../components/Spinner';
import FakeReviews from '../components/FakeReviews';

const EADetails = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);
  const { addToWishlist, removeFromWishlist, isInWishlist } = useContext(WishlistContext);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [activeTab, setActiveTab] = useState('Overview');

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top when page loads
    fetch(`${API_URL}/api/products/${id}`)
      .then(res => res.json())
      .then(data => {
        if (!data.error) {
          setProduct(data);
          // Save to recently viewed
          try {
            let viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
            viewed = viewed.filter(p => p.id !== data.id); // Remove if already exists
            viewed.unshift(data); // Add to front
            if (viewed.length > 4) viewed = viewed.slice(0, 4); // Keep latest 4
            localStorage.setItem('recentlyViewed', JSON.stringify(viewed));
          } catch (e) {
            console.error('Local storage error', e);
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center pt-24"><Spinner text="Loading EA Details..." /></div>;
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-silver-text mb-4">EA Not Found</h2>
          <Link to="/marketplace" className="text-primary hover:underline">Return to Marketplace</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-12 md:py-24 pt-24 relative">
      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-24 right-4 z-50 bg-[#131418] border border-primary/20 p-4 rounded-xl shadow-2xl shadow-primary/10 flex items-center gap-4 animate-fade-in">
          <div className="bg-primary/20 p-2 rounded-full flex items-center justify-center">
            <span className="material-symbols-outlined text-primary">check_circle</span>
          </div>
          <div>
            <h4 className="text-white font-bold text-sm">Success</h4>
            <p className="text-on-surface-variant text-xs">"{product.name}" added to cart!</p>
          </div>
          <button onClick={() => setShowToast(false)} className="text-on-surface-variant hover:text-white ml-2">
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      )}

      {/* Hero Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-32">
        {/* Left: Character Image Stack */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="relative"
        >
          <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full opacity-50"></div>
          <div className="relative glass-panel rounded-xl overflow-hidden border-primary/20 hero-glow">
            <img 
              alt={`${product.name} Logo`} 
              className="w-full aspect-square object-cover" 
              src={product.image ? getValidImageUrl(product.image) : "https://lh3.googleusercontent.com/aida/AP1WRLu3VM-9RrL8h0xwIcA8NzexoXX3SCvg6Ng64WiIwKsoA4SUZh3JOguhErzieVdekccqQhe_hTN3ZEaVsGBYwRwjPzAbGmrIdZklGxRMMxLoQto9Alpg9A77l-jWpAX6XFn7WGVw_CddPtgFLwZLRrH7-44IlMvufeyoIJoV-agMbSuW6uo420eY4V9C9gWgOD-vb15sbJHyPm6SgEDPygR1OBv1YizEtn9UNIusHPgsoS_Whm30nap0dCw2"}
            />
            {/* Overlay Badge */}
            <div className="absolute top-6 right-6 bg-surface-container-highest/80 backdrop-blur-lg border border-primary/30 px-4 py-2 rounded-lg flex items-center gap-3">
              <span className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-secondary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-secondary"></span>
              </span>
              <span className="font-label-sm text-label-sm text-secondary uppercase tracking-widest">Live Signal Active</span>
            </div>
          </div>
        </motion.div>

        {/* Right: Product Details */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex flex-col gap-8"
        >
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="bg-primary/20 text-primary px-3 py-1 text-label-sm font-label-sm rounded uppercase tracking-wider">Premium EA</span>
              <div className="flex text-primary">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>star</span>
              </div>
            </div>
            <h1 className="font-display-lg text-display-lg gold-gradient-text mb-2">{product.name}</h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-xl">
              {product.tagline || product.description}
            </p>
          </div>

          {/* Core Stats Grid */}
          <div className="grid grid-cols-3 gap-4">
            <div className="glass-panel p-4 rounded-lg border-white/5">
              <p className="text-label-sm font-label-sm text-on-surface-variant mb-1 uppercase">Win Rate</p>
              <p className="text-headline-sm font-headline-sm text-secondary">{product.winRate || "78.18%"}</p>
            </div>
            <div className="glass-panel p-4 rounded-lg border-white/5">
              <p className="text-label-sm font-label-sm text-on-surface-variant mb-1 uppercase">Drawdown</p>
              <p className="text-headline-sm font-headline-sm text-danger-red">{product.maxDrawdown || "8.08%"}</p>
            </div>
            <div className="glass-panel p-4 rounded-lg border-white/5">
              <p className="text-label-sm font-label-sm text-on-surface-variant mb-1 uppercase">Profit Factor</p>
              <p className="text-headline-sm font-headline-sm text-primary">4.12</p>
            </div>
          </div>

          <div className="flex flex-col gap-6 pt-4">
            <div className="flex items-end gap-3">
              <span className="text-display-lg-mobile font-display-lg-mobile text-silver-text">${product.newPrice}</span>
              <span className="text-body-md font-body-md text-on-surface-variant line-through mb-2">${product.oldPrice}</span>
              <span className="bg-secondary/10 text-secondary px-2 py-1 rounded text-label-sm mb-2">SAVE 91%</span>
            </div>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  setIsAdding(true);
                  setTimeout(() => {
                    addToCart(product);
                    setIsAdding(false);
                    setShowToast(true);
                    setTimeout(() => setShowToast(false), 3000);
                  }, 800);
                }}
                disabled={isAdding}
                className={`font-bold py-4 px-8 rounded-lg text-label-md flex items-center justify-center gap-3 transition-all duration-300 disabled:opacity-80 ${isAdding ? 'bg-primary-fixed-dim text-on-primary-fixed scale-95 shadow-inner' : 'bg-primary text-on-primary-fixed hover:bg-primary-fixed-dim hover:scale-[1.02] active:scale-95'}`}
              >
                {isAdding ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">autorenew</span>
                    Adding...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">shopping_cart</span>
                    Add to Cart
                  </>
                )}
              </button>
              <button className="border border-white/20 text-silver-text font-bold py-4 px-8 rounded-lg text-label-md flex items-center gap-3 transition-all hover:bg-white/5 hover:border-primary/50">
                <span className="material-symbols-outlined">description</span>
                Live Signal
              </button>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  if (isInWishlist(product.id)) {
                    removeFromWishlist(product.id);
                  } else {
                    addToWishlist(product.id, {
                      name: product.name,
                      price: product.price,
                      newPrice: product.newPrice,
                      image: product.image
                    });
                  }
                }}
                className={`w-14 h-[56px] flex items-center justify-center rounded-lg border transition-all ${isInWishlist(product.id) ? 'bg-primary/20 border-primary text-primary hover:bg-primary/30' : 'bg-transparent border-white/20 text-silver-text hover:bg-white/5 hover:border-primary/50'}`}
                aria-label="Toggle Wishlist"
              >
                <span className={`material-symbols-outlined text-[24px] ${isInWishlist(product.id) ? 'filled' : ''}`}>favorite</span>
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-white/5">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">verified</span>
              <span className="text-label-sm font-label-sm">MT4 / MT5 Verified</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">security</span>
              <span className="text-label-sm font-label-sm">Lifetime Access</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">update</span>
              <span className="text-label-sm font-label-sm">Free Updates</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Strategy & Backtest Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-gutter-desktop mb-32">
        {/* Left 2 Columns: Tabs Area */}
        <div className="lg:col-span-2">
          {/* Tabs Header */}
          <div className="flex overflow-x-auto border-b border-white/10 mb-8 hide-scrollbar">
            {['Overview', 'Backtest Results', 'Reviews', 'Faq'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-4 font-bold whitespace-nowrap border-b-2 transition-colors ${activeTab === tab ? 'border-primary text-primary' : 'border-transparent text-silver-text hover:text-white'}`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[400px]">
            {activeTab === 'Overview' && (
              <div className="animate-fade-in">
                <h2 className="text-[20px] font-bold text-white mb-6">Product Description</h2>
                <div className="space-y-6 text-on-surface-variant font-body-md leading-relaxed text-[15px]">
                  {product.longDescription ? (
                    product.longDescription.split('\n\n').map((paragraph, idx) => (
                      <p key={idx}>{paragraph}</p>
                    ))
                  ) : (
                    <p>{product.description}</p>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                    <ul className="space-y-3">
                      {product.extendedFeatures && product.extendedFeatures.slice(0, Math.ceil(product.extendedFeatures.length / 2)).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="material-symbols-outlined text-primary mt-1">check_circle</span>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <ul className="space-y-3">
                      {product.extendedFeatures && product.extendedFeatures.slice(Math.ceil(product.extendedFeatures.length / 2)).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="material-symbols-outlined text-primary mt-1">check_circle</span>
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'Backtest Results' && (
              <div className="animate-fade-in">
                <h2 className="text-[20px] font-bold text-white mb-6">Verified Strategy Tester Reports</h2>
                {product.backtestImage ? (
                  <div className="glass-panel p-2 rounded-xl overflow-hidden border border-white/10">
                    <img 
                      alt={`${product.name} Backtest Results`} 
                      className="w-full object-contain rounded-lg cursor-zoom-in hover:opacity-90 transition-opacity" 
                      src={getValidImageUrl(product.backtestImage)} 
                      onClick={() => setIsZoomed(true)}
                    />
                  </div>
                ) : (
                  <div className="p-8 text-center bg-surface-container-low rounded-xl border border-white/5">
                    <p className="text-on-surface-variant">No backtest results have been uploaded for this EA.</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'Reviews' && (
              <div className="animate-fade-in">
                <FakeReviews productId={product.id} productName={product.name} />
              </div>
            )}

            {activeTab === 'Faq' && (
              <div className="animate-fade-in">
                <h2 className="text-[20px] font-bold text-white mb-6">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  <div className="bg-surface-container border border-white/5 p-6 rounded-xl">
                    <h4 className="font-bold text-primary mb-3">Will this work with a small account?</h4>
                    <p className="text-on-surface-variant text-[14px] leading-relaxed">Yes, the minimum recommended deposit is usually around $100, though $500+ is optimal for better risk management depending on the lot size you choose.</p>
                  </div>
                  <div className="bg-surface-container border border-white/5 p-6 rounded-xl">
                    <h4 className="font-bold text-primary mb-3">Do I need a VPS?</h4>
                    <p className="text-on-surface-variant text-[14px] leading-relaxed">Yes, for Expert Advisors to run 24/5 without interruption, a Virtual Private Server (VPS) is highly recommended so your home PC doesn't need to stay on.</p>
                  </div>
                  <div className="bg-surface-container border border-white/5 p-6 rounded-xl">
                    <h4 className="font-bold text-primary mb-3">Which broker is best?</h4>
                    <p className="text-on-surface-variant text-[14px] leading-relaxed">This EA works with any broker, but we highly recommend an ECN broker with low spreads (like IC Markets, Pepperstone, or Tickmill) for the best execution.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Specs & Downloads */}
        <div className="space-y-12">
          <section>
            <h3 className="text-headline-sm font-headline-sm mb-6">Technical Specifications</h3>
            <div className="glass-panel rounded-xl overflow-hidden">
              <div className="p-4 border-b border-white/5 flex justify-between">
                <span className="text-on-surface-variant">Platform</span>
                <span className="text-primary font-medium">{product.platform || (product.id.includes('v1') || product.id.includes('gold') ? 'MetaTrader 5' : 'MetaTrader 4 / 5')}</span>
              </div>
              <div className="p-4 border-b border-white/5 flex justify-between">
                <span className="text-on-surface-variant">Account Type</span>
                <span className="text-primary font-medium">{product.accountType || 'ECN / Low Spread'}</span>
              </div>
              <div className="p-4 border-b border-white/5 flex justify-between">
                <span className="text-on-surface-variant">Currency Pairs</span>
                <span className="text-primary font-medium">{product.currencyPairs || (product.id.includes('gold') ? 'XAUUSD' : 'Any (Prop: EURUSD)')}</span>
              </div>
              <div className="p-4 border-b border-white/5 flex justify-between">
                <span className="text-on-surface-variant">Min. Deposit</span>
                <span className="text-primary font-medium">{product.minDeposit || '$100'}</span>
              </div>
              <div className="p-4 border-b border-white/5 flex justify-between">
                <span className="text-on-surface-variant">Leverage</span>
                <span className="text-primary font-medium">{product.leverage || '1:30 to 1:500'}</span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-on-surface-variant">Timeframe</span>
                <span className="text-primary font-medium">{product.timeframe || 'M1 / M5 / M15'}</span>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-headline-sm font-headline-sm mb-6">What's Included</h3>
            <div className="space-y-4">
              {product.whatsIncluded && product.whatsIncluded.length > 0 ? (
                product.whatsIncluded.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 glass-panel p-4 rounded-xl">
                    <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary">{item.icon || 'terminal'}</span>
                    </div>
                    <div>
                      <p className="font-bold">{item.title}</p>
                      <p className="text-label-sm text-on-surface-variant">{item.subtitle}</p>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  <div className="flex items-center gap-4 glass-panel p-4 rounded-xl">
                    <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary">terminal</span>
                    </div>
                    <div>
                      <p className="font-bold">EA Files (.ex4 &amp; .ex5)</p>
                      <p className="text-label-sm text-on-surface-variant">Ready for both platforms</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 glass-panel p-4 rounded-xl">
                    <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary">settings</span>
                    </div>
                    <div>
                      <p className="font-bold">Optimized Set Files</p>
                      <p className="text-label-sm text-on-surface-variant">Best settings for top pairs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 glass-panel p-4 rounded-xl">
                    <div className="w-12 h-12 bg-primary/10 rounded flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary">menu_book</span>
                    </div>
                    <div>
                      <p className="font-bold">Installation Guide</p>
                      <p className="text-label-sm text-on-surface-variant">Step-by-step PDF &amp; Video</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>

          {/* CTA Card */}
          <div className="glass-panel p-8 rounded-xl border-primary/20 bg-primary/5 relative overflow-hidden group">
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/10 blur-3xl group-hover:scale-150 transition-transform"></div>
            <p className="text-label-sm font-label-sm uppercase tracking-widest text-primary mb-2">Limited Offer</p>
            <h4 className="text-headline-sm font-headline-sm mb-4">Start Trading Like a Pro Today</h4>
            <p className="text-body-md text-on-surface-variant mb-6">Join 1,200+ traders using {product.name} to automate their portfolios.</p>
            <button 
              onClick={(e) => {
                e.preventDefault();
                setIsAdding(true);
                setTimeout(() => {
                  addToCart(product);
                  setIsAdding(false);
                  setShowToast(true);
                  setTimeout(() => setShowToast(false), 3000);
                }, 800);
              }}
              disabled={isAdding}
              className={`w-full py-4 rounded-lg font-bold shadow-lg transition-all ${isAdding ? 'bg-primary-fixed-dim text-on-primary-fixed scale-95 shadow-inner' : 'bg-primary text-on-primary hover:shadow-primary/40 hover:scale-[1.02] active:scale-[0.98]'}`}
            >
              {isAdding ? 'Adding to Cart...' : `Buy Now - ${product.name}`}
            </button>
          </div>
        </div>
      </div>

      {/* Backtest Zoom Modal */}
      {isZoomed && product.backtestImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-12 cursor-zoom-out animate-fade-in"
          onClick={() => setIsZoomed(false)}
        >
          <button 
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition z-10"
            onClick={(e) => { e.stopPropagation(); setIsZoomed(false); }}
          >
            <span className="material-symbols-outlined text-[24px]">close</span>
          </button>
          <img 
            alt={`${product.name} Backtest Zoomed`} 
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" 
            src={getValidImageUrl(product.backtestImage)} 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}
    </main>
  );
};

export default EADetails;
